import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Clock, User, ArrowLeft, Stethoscope, Headphones } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import api from "@/lib/api";

export default function SearchPage() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [results, setResults] = useState({ articles: [], doctors: [], podcasts: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = params.get("q");
    if (q) {
      setQuery(q);
      doSearch(q);
    }
  }, [params]);

  const doSearch = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSearch(query);
  };

  const total = (results.articles?.length || 0) + (results.doctors?.length || 0) + (results.podcasts?.length || 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="search-page">
      <SEOHead title={params.get("q") ? `Search: ${params.get("q")}` : "Search"} description="Search medical articles, doctors, and podcasts on Pulse Medical Media." type="website" />
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground mb-6" data-testid="back-home">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-6">Search</h1>

      <form onSubmit={handleSubmit} className="flex gap-3 mb-8">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles, doctors, podcasts..."
          className="text-base h-12"
          data-testid="search-page-input"
        />
        <Button type="submit" className="h-12 px-6 bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="search-submit">
          <Search className="h-4 w-4 mr-2" /> Search
        </Button>
      </form>

      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : (
        <>
          {params.get("q") && <p className="text-sm font-sans text-muted-foreground mb-6">{total} results for "{params.get("q")}"</p>}

          {results.articles?.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xl font-serif font-bold text-foreground mb-4">Articles</h2>
              <div className="space-y-4">
                {results.articles.map((art) => (
                  <Link key={art.id} to={`/article/${art.id}`} className="flex gap-4 p-4 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group" data-testid={`search-result-${art.id}`}>
                    {art.featured_image && <img src={art.featured_image} alt="" className="w-24 h-20 object-cover shrink-0" loading="lazy" />}
                    <div className="min-w-0">
                      <h3 className="font-serif font-bold text-foreground group-hover:text-[#1E88E5] transition-colors line-clamp-2">{art.title}</h3>
                      <p className="text-sm font-sans text-muted-foreground mt-1 line-clamp-2">{art.summary}</p>
                      <div className="flex gap-3 mt-2 text-xs font-sans text-muted-foreground">
                        {art.author_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{art.author_name}</span>}
                        {art.reading_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{art.reading_time}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.doctors?.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xl font-serif font-bold text-foreground mb-4">Doctors</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {results.doctors.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-4 p-4 border border-slate-200 dark:border-slate-700" data-testid={`search-doctor-${doc.id}`}>
                    <img src={doc.profile_image} alt={doc.name} className="w-14 h-14 rounded-full object-cover" loading="lazy" />
                    <div>
                      <h3 className="font-serif font-bold text-foreground">{doc.name}</h3>
                      <p className="text-sm font-sans text-[#1E88E5] flex items-center gap-1"><Stethoscope className="h-3 w-3" />{doc.specialization}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.podcasts?.length > 0 && (
            <div>
              <h2 className="text-xl font-serif font-bold text-foreground mb-4">Podcasts</h2>
              <div className="space-y-4">
                {results.podcasts.map((pod) => (
                  <Link key={pod.id} to={`/podcast/${pod.id}`} className="flex items-center gap-4 p-4 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid={`search-podcast-${pod.id}`}>
                    <img src={pod.cover_image} alt={pod.title} className="w-16 h-16 object-cover shrink-0" loading="lazy" />
                    <div>
                      <h3 className="font-serif font-bold text-foreground flex items-center gap-2"><Headphones className="h-4 w-4 text-[#1E88E5]" />{pod.title}</h3>
                      <p className="text-sm font-sans text-muted-foreground">{pod.host_name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {total === 0 && params.get("q") && (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-lg font-sans text-muted-foreground">No results found for "{params.get("q")}"</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
